import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Home, Search, Filter, Eye } from "lucide-react";
import Layout from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { API } from "../../App";
import {
  UNIT_STATUS_LABELS, UNIT_STATUS_COLORS, formatINR, fmtDate, fmtDateTime,
  TENANT_STAGE_LABELS, LEASE_STATUS_LABELS, SERVICE_STATUS_LABELS,
} from "./_rentalHelpers";

const emptyUnit = {
  project_name: "", tower: "", unit_number: "", configuration: "", floor: "",
  area_sqft: "", furnishing_status: "unfurnished", parking_included: false,
  owner_name: "", owner_phone: "", owner_email: "",
  expected_rent: "", minimum_rent: "", security_deposit: "", maintenance_included: false,
  preferred_tenant_type: "any", available_from: "",
  status: "owner_interested", notes: "",
};

const UnitForm = ({ value, onChange, meta }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
      <div className="col-span-2 grid grid-cols-3 gap-3">
        <div><Label>Project name</Label><Input data-testid="form-project-name" value={value.project_name} onChange={e => set("project_name", e.target.value)} /></div>
        <div><Label>Tower</Label><Input value={value.tower} onChange={e => set("tower", e.target.value)} /></div>
        <div><Label>Unit no *</Label><Input data-testid="form-unit-number" value={value.unit_number} onChange={e => set("unit_number", e.target.value)} /></div>
      </div>
      <div><Label>Configuration</Label><Input placeholder="2BHK / 3BHK" value={value.configuration} onChange={e => set("configuration", e.target.value)} /></div>
      <div><Label>Floor</Label><Input value={value.floor} onChange={e => set("floor", e.target.value)} /></div>
      <div><Label>Area (sqft)</Label><Input type="number" value={value.area_sqft} onChange={e => set("area_sqft", e.target.value)} /></div>
      <div>
        <Label>Furnishing</Label>
        <Select value={value.furnishing_status} onValueChange={v => set("furnishing_status", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {(meta?.furnishing_options || ["unfurnished", "semi", "full"]).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Preferred Tenant</Label>
        <Select value={value.preferred_tenant_type} onValueChange={v => set("preferred_tenant_type", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {(meta?.preferred_tenant_types || ["family", "bachelor", "corporate", "any"]).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2 pt-7">
        <Switch checked={value.parking_included} onCheckedChange={v => set("parking_included", v)} /> <span className="text-sm">Parking included</span>
      </div>
      <div className="flex items-center gap-2 pt-7">
        <Switch checked={value.maintenance_included} onCheckedChange={v => set("maintenance_included", v)} /> <span className="text-sm">Maintenance included</span>
      </div>
      <div className="col-span-2 border-t pt-3 mt-1 grid grid-cols-3 gap-3">
        <div><Label>Owner name *</Label><Input data-testid="form-owner-name" value={value.owner_name} onChange={e => set("owner_name", e.target.value)} /></div>
        <div><Label>Owner phone *</Label><Input data-testid="form-owner-phone" value={value.owner_phone} onChange={e => set("owner_phone", e.target.value)} /></div>
        <div><Label>Owner email</Label><Input value={value.owner_email} onChange={e => set("owner_email", e.target.value)} /></div>
      </div>
      <div className="col-span-2 grid grid-cols-3 gap-3">
        <div><Label>Expected rent</Label><Input type="number" value={value.expected_rent} onChange={e => set("expected_rent", e.target.value)} /></div>
        <div><Label>Minimum rent</Label><Input type="number" value={value.minimum_rent} onChange={e => set("minimum_rent", e.target.value)} /></div>
        <div><Label>Security deposit</Label><Input type="number" value={value.security_deposit} onChange={e => set("security_deposit", e.target.value)} /></div>
      </div>
      <div>
        <Label>Available from</Label>
        <Input type="date" value={value.available_from || ""} onChange={e => set("available_from", e.target.value)} />
      </div>
      <div>
        <Label>Status</Label>
        <Select value={value.status} onValueChange={v => set("status", v)}>
          <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(UNIT_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={value.notes} onChange={e => set("notes", e.target.value)} /></div>
    </div>
  );
};

const DetailSection = ({ title, items, render, empty }) => (
  <div className="mt-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</p>
    {(items || []).length === 0 ? (
      <p className="text-sm text-slate-400 italic">{empty || "No records"}</p>
    ) : (
      <div className="space-y-1.5">{items.map(render)}</div>
    )}
  </div>
);

export default function RentalUnitsPage() {
  const [units, setUnits] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUnit);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const [u, m] = await Promise.all([
        axios.get(`${API}/rentals/units`, { params }),
        meta ? Promise.resolve({ data: meta }) : axios.get(`${API}/rentals/meta`),
      ]);
      setUnits(u.data || []);
      setMeta(m.data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load units");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statusFilter]);
  // Search is debounced by user pressing Enter
  const onSearchKey = (e) => { if (e.key === "Enter") load(); };

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      ...emptyUnit, ...u,
      area_sqft: u.area_sqft ?? "",
      expected_rent: u.expected_rent ?? "",
      minimum_rent: u.minimum_rent ?? "",
      security_deposit: u.security_deposit ?? "",
      available_from: u.available_from || "",
    });
    setShowCreate(true);
  };

  const startCreate = () => {
    setEditing(null);
    setForm(emptyUnit);
    setShowCreate(true);
  };

  const submit = async () => {
    if (!form.unit_number?.trim() || !form.owner_name?.trim() || !form.owner_phone?.trim()) {
      toast.error("Unit number, owner name and phone are required");
      return;
    }
    const payload = {
      ...form,
      area_sqft: form.area_sqft === "" ? null : Number(form.area_sqft),
      expected_rent: form.expected_rent === "" ? null : Number(form.expected_rent),
      minimum_rent: form.minimum_rent === "" ? null : Number(form.minimum_rent),
      security_deposit: form.security_deposit === "" ? null : Number(form.security_deposit),
    };
    try {
      if (editing) {
        await axios.put(`${API}/rentals/units/${editing.rental_unit_id}`, payload);
        toast.success("Unit updated");
      } else {
        await axios.post(`${API}/rentals/units`, payload);
        toast.success("Unit created");
      }
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const openDetail = async (unit) => {
    try {
      const res = await axios.get(`${API}/rentals/units/${unit.rental_unit_id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error("Failed to load unit details");
    }
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto" data-testid="rental-units-page">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Home className="w-6 h-6 text-emerald-600" /> Rental Units
            </h1>
            <p className="text-sm text-slate-500">Owner pipeline, listings & inventory</p>
          </div>
          <Button onClick={startCreate} data-testid="create-unit-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-1" /> New Unit
          </Button>
        </div>

        <Card className="p-3 mb-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400" />
            <Input
              data-testid="unit-search"
              placeholder="Search unit no / owner / project (Enter)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={onSearchKey}
            />
          </div>
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px]" data-testid="unit-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(UNIT_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </Card>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Unit</th>
                  <th className="px-3 py-2 text-left">Owner</th>
                  <th className="px-3 py-2 text-left">Rent</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Updated</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Loading…</td></tr>}
                {!loading && units.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-400">No units yet. Create your first one.</td></tr>
                )}
                {units.map(u => (
                  <tr key={u.rental_unit_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`unit-row-${u.rental_unit_id}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium text-slate-900">{u.project_name || "—"} {u.tower ? `· ${u.tower}` : ""}</p>
                      <p className="text-xs text-slate-500">Unit {u.unit_number} · {u.configuration || "—"} · {u.area_sqft ? `${u.area_sqft} sqft` : "—"}</p>
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium">{u.owner_name}</p>
                      <p className="text-xs text-slate-500">{u.owner_phone}</p>
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium">{formatINR(u.expected_rent)}</p>
                      <p className="text-xs text-slate-500">Min {formatINR(u.minimum_rent)}</p>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={UNIT_STATUS_COLORS[u.status] || "bg-slate-100"}>{UNIT_STATUS_LABELS[u.status] || u.status}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(u.updated_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(u)} data-testid={`view-unit-${u.rental_unit_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => startEdit(u)} data-testid={`edit-unit-${u.rental_unit_id}`}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Create/Edit dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Unit" : "New Rental Unit"}</DialogTitle>
            </DialogHeader>
            <UnitForm value={form} onChange={setForm} meta={meta} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="submit-unit" className="bg-emerald-600 hover:bg-emerald-700">
                {editing ? "Save changes" : "Create unit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail drawer */}
        <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <SheetContent side="right" className="max-w-lg w-full sm:max-w-xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{detail ? `Unit ${detail.unit_number}` : ""}</SheetTitle>
            </SheetHeader>
            {detail && (
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Badge className={UNIT_STATUS_COLORS[detail.status] || "bg-slate-100"}>
                    {UNIT_STATUS_LABELS[detail.status] || detail.status}
                  </Badge>
                  <span className="text-slate-500 text-xs">Updated {fmtDateTime(detail.updated_at)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Project:</span> {detail.project_name || "—"} {detail.tower ? `· ${detail.tower}` : ""}</div>
                  <div><span className="text-slate-500">Config:</span> {detail.configuration || "—"}</div>
                  <div><span className="text-slate-500">Area:</span> {detail.area_sqft ? `${detail.area_sqft} sqft` : "—"}</div>
                  <div><span className="text-slate-500">Floor:</span> {detail.floor || "—"}</div>
                  <div><span className="text-slate-500">Furnishing:</span> {detail.furnishing_status || "—"}</div>
                  <div><span className="text-slate-500">Parking:</span> {detail.parking_included ? "Yes" : "No"}</div>
                </div>
                <div className="border-t pt-2 grid grid-cols-2 gap-2">
                  <div><span className="text-slate-500">Owner:</span> {detail.owner_name}</div>
                  <div><span className="text-slate-500">Phone:</span> {detail.owner_phone}</div>
                  <div><span className="text-slate-500">Expected:</span> {formatINR(detail.expected_rent)}</div>
                  <div><span className="text-slate-500">Min rent:</span> {formatINR(detail.minimum_rent)}</div>
                  <div><span className="text-slate-500">Deposit:</span> {formatINR(detail.security_deposit)}</div>
                  <div><span className="text-slate-500">Available:</span> {fmtDate(detail.available_from)}</div>
                </div>
                {detail.notes && (
                  <div className="bg-slate-50 p-2 rounded text-slate-700">{detail.notes}</div>
                )}

                <DetailSection
                  title="Visits"
                  items={detail.visits}
                  empty="No visits scheduled"
                  render={(v) => (
                    <div key={v.rental_visit_id} className="text-xs p-2 rounded border border-slate-100 bg-slate-50">
                      {fmtDateTime(v.scheduled_at)} · {v.status} {v.feedback ? `· ${v.feedback.slice(0, 60)}` : ""}
                    </div>
                  )}
                />
                <DetailSection
                  title="Leases"
                  items={detail.leases}
                  empty="No leases yet"
                  render={(l) => (
                    <div key={l.lease_id} className="text-xs p-2 rounded border border-slate-100 bg-slate-50">
                      {LEASE_STATUS_LABELS[l.status] || l.status} · {fmtDate(l.lease_start)} → {fmtDate(l.lease_end)} · {formatINR(l.monthly_rent)}
                    </div>
                  )}
                />
                <DetailSection
                  title="Service Requests"
                  items={detail.service_requests}
                  empty="No service requests"
                  render={(s) => (
                    <div key={s.service_request_id} className="text-xs p-2 rounded border border-slate-100 bg-slate-50">
                      {s.category} · {SERVICE_STATUS_LABELS[s.status] || s.status} · {s.description?.slice(0, 80)}
                    </div>
                  )}
                />
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
